"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { heroStats } from "@/lib/mockData";
import { useLang } from "@/lib/i18n/LanguageContext";
import Icon from "@/components/ui/Icon/Icon";

const INTERVAL = 5000;

interface HeroProps {
  slides: string[];
}

export default function Hero({ slides }: HeroProps) {
  const [current, setCurrent] = useState(0);
  const [mounted, setMounted] = useState(false);
  const total = slides.length || 1;
  const { t } = useLang();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const goToSlide = useCallback((index: number) => {
    setCurrent(index);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % total);
  }, [total]);

  const prevSlide = useCallback(() => {
    setCurrent((prev) => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    if (total <= 1) return;
    const id = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, INTERVAL);
    return () => clearInterval(id);
  }, [total]);

  const featKeys = [
    "hero.feat.europe",
    "hero.feat.translate",
    "hero.feat.export",
    "hero.feat.logistics",
    "hero.feat.safe",
  ];

  const statIcons = ["users", "globe", "bar-chart", "headphones"];
  const statKeys = [
    "hero.stat.farmers",
    "hero.stat.countries",
    "hero.stat.exported",
    "hero.stat.support",
  ];

  return (
    <section className="hero">
      {slides.map((src, i) => (
        <div
          key={i}
          className={`hero--bg-slide${i === current && mounted ? ' is-active' : ''}`}
          style={{ backgroundImage: `url(${src})` }}
        />
      ))}
      <div className="hero--bg-overlay" />

      <div className="hero--inner">
        <div className="hero--content">
          <p className="hero--breadcrumb">
            <Icon className="hero--breadcrumb-icon" name="map-pin" size={14} />
            {t("hero.breadcrumb")}
          </p>

          <h1 className="hero--title anim-slide-up">
            {t("hero.title_join")}{" "}
            <span className="hero--em"><span className="hero--em-euro">Euro</span><span className="hero--em-farm">farm</span></span>
            <br />{t("hero.title_sell")}
            <br />{t("hero.title_region")}
            <br /><span className="hero--green">{t("hero.title_moldova")}</span>
          </h1>

          <p className="hero--sub anim-slide-up anim-delay-1">
            {t("hero.subtitle")}
          </p>

          <div className="hero--ctas anim-slide-up anim-delay-2">
            <a className="hero--btn-primary" href="#join">
              {t("hero.cta_primary")}
              <Icon className="hero--btn-icon" name="arrow-right" size={16} />
            </a>
            <a className="hero--btn-ghost" href="#about">{t("hero.cta_secondary")}</a>
          </div>

          <div className="hero--features">
            {featKeys.map((key, i) => {
              const label = t(key);
              return (
                <div key={key} className={`hero--feature anim-slide-up anim-delay-${i + 3}`}>
                  <Icon className="hero--feature-icon" name="shield-check" size={28} />
                  <span className="hero--feature-label">
                    {label.split("\n").map((line, i, arr) => (
                      <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                    ))}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="hero--aside">
          <div className="hero--stats">
            {heroStats.map((s, i) => (
              <div key={statKeys[i]} className={`hero--stat-card anim-slide-right anim-delay-${i + 1}`}>
                <Icon className="hero--stat-icon" name={statIcons[i]} size={32} />
                <div className="hero--stat-body">
                  <strong className="hero--stat-value">{s.value}</strong>
                  <span className="hero--stat-label">{t(statKeys[i])}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="hero--carousel">
            <button
              className="hero--arrow"
              aria-label={t("slider.prev")}
              onClick={prevSlide}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              className="hero--arrow"
              aria-label={t("slider.next")}
              onClick={nextSlide}
            >
              <ChevronRight size={18} />
            </button>
            <div className="hero--dots">
              {slides.map((_, i) => (
                <button
                  key={i}
                  className={`hero--dot${i === current ? ' is-active' : ''}`}
                  aria-label={`${t("slider.slide")} ${i + 1}`}
                  onClick={() => goToSlide(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
